import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { BehaviorSubject ,  Observable ,  fromEvent } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';

export interface IWindowSize {
  width: number;
  height: number;
}

export enum BrowserMode {
  Desktop = 'desktop',
  Tablet = 'tablet',
  Mobile = 'mobile',
}

/**
 * A service that provides information about the current browser state
 * @class
 *
 * Ideally we would subscribe to the browser state directly in a template using the async pipe
 * rather that creating local state and subscriptions
 */
@Injectable()
export class BrowserService {

  public mode$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public windowSize$: BehaviorSubject<IWindowSize> = new BehaviorSubject<IWindowSize>({ width: 0, height: 0 });

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {

    if (isPlatformBrowser(this.platformId)) {

      fromEvent(window, 'resize').pipe(
        map(() => {
          return {
            width : document.documentElement.clientWidth,
            height : document.documentElement.clientHeight
          };
        })).pipe(startWith({
          width : document.documentElement.clientWidth,
          height : document.documentElement.clientHeight
        })).subscribe((data: any) => {
          this.emitCurrentMode(data.width);
          this.emitCurrentWindowSize(data);
        });
    }

  }

  /**
   * Determines the current mode based on the browser width and emits that value to any mode$ subscribers
   * @param {number}
   */
  emitCurrentMode(windowWidth: number) {

    let mode: string = BrowserMode.Desktop;

    if (windowWidth < 992)
      mode = BrowserMode.Tablet;

    if (windowWidth < 768)
      mode = BrowserMode.Mobile;

    // broadcast new mode to all subscribers
    this.mode$.next(mode);
  }

  /**
   * Emits new window size changes to any windowSize$ subscribers
   * @param {obj:WindowSizeModel}
   */
  emitCurrentWindowSize(obj: IWindowSize) {

    this.windowSize$.next(obj);
  }

  /**
   * Returns the mode$ BehaviorSubject that can be subscribed to
   * @return {BehaviorSubject}
   */
  getMode(): BehaviorSubject<string> {
    return this.mode$;
  }

  /**
   * Returns the windowSize$ BehaviorSubject that can be subscribed to
   * @return {BehaviorSubject}
   */
  getWindowSize(): BehaviorSubject<IWindowSize> {
    return this.windowSize$;
  }

  /**
   * Helper function to detects touch devices based on user agent
   * @return {boolean}
   */
  isTouchDevice(): boolean {

    // tslint:disable-next-line:max-line-length
    if (navigator.userAgent.match(/Android/i) || navigator.userAgent.match(/iPhone/i) || navigator.userAgent.match(/iPad/i) || navigator.userAgent.match(/iPod/i) || navigator.userAgent.match(/BlackBerry/i) || navigator.userAgent.match(/webOS/i)) {
      return true;
    } else {
      return false;
    }
  }

}
